import { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { FORMS_PATIENT_BASE_URL } from '@/lib/formsUrls';

/**
 * FORMS-5: Replaces v2's questionnaire renderer.
 * Redirects the browser to forms.clinio.dk/q/<token>, preserving all query params.
 * window.location.replace is used so this page does not appear in browser history.
 */
export function QuestionnaireRedirect() {
  const { token } = useParams<{ token: string }>();
  const { search } = useLocation();
  const { t } = useTranslation();

  const formsUrl = token
    ? `${FORMS_PATIENT_BASE_URL}/q/${encodeURIComponent(token)}${search}`
    : null;

  useEffect(() => {
    if (formsUrl) {
      window.location.replace(formsUrl);
    }
  }, [formsUrl]);

  if (!formsUrl) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
        <p className="text-sm text-red-600 dark:text-red-400">
          {t('questionnaires.redirect_invalid_token')}
        </p>
      </main>
    );
  }

  return (
    <main
      lang="da"
      className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50 px-4 dark:bg-gray-900"
    >
      <Loader2
        aria-hidden="true"
        className="h-8 w-8 animate-spin text-indigo-600 dark:text-indigo-400"
      />
      <p className="text-base font-medium text-gray-700 dark:text-gray-300">
        {t('questionnaires.redirect_title')}
      </p>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        {t('questionnaires.redirect_message')}
      </p>
      <a
        href={formsUrl}
        className="mt-2 text-sm text-indigo-600 underline underline-offset-4 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-200"
      >
        {t('questionnaires.redirect_link')}
      </a>
    </main>
  );
}
